/**
 * Entity prefix registry — the central map from prefix to table metadata.
 *
 * Powers both ID encoding/decoding and the generic org-lookup middleware.
 * To add a new entity prefix, add an entry here and the system picks it up.
 *
 * @module EntityRegistry
 * @see docs/project/prefixed-ids.md
 */

export const EntityPrefix = {
  agentEval: 'aeval',
  agentEvalCase: 'aecase',
  agentEvalRun: 'aerun',
  agentEvalAttempt: 'aeatt',
  organization: 'org',
  workflow: 'wf',
  workflowTestExecution: 'exec',
  workflowTestRun: 'trun',
  workflowRun: 'wrun',
  adhocTest: 'adhoc',
  exploratoryTest: 'expt',
  perfTest: 'ptest',
  perfRun: 'prun',
  issue: 'iss',
  property: 'prop',
  credential: 'cred',
  credentialTemplate: 'ctpl',
  syntheticUniverse: 'su',
  syntheticUser: 'sp',
  syntheticUserRun: 'sr',
  userEvent: 'uev',
  userEventDashboard: 'ued',
  userEventDashboardCard: 'uedc',
  triageRun: 'trg',
  triageFinding: 'trgf',
  phoneNumber: 'phn',
  textMessage: 'sms',
  productArea: 'parea',
  productCapability: 'pcap',
  productCapabilityRule: 'prule',
  landingPage: 'lp',
  prReview: 'prr',
  digest: 'dig',
  explorationCycle: 'excy',
  explorationLead: 'exld',
  testSignInProvider: 'tsp',
  testSignInClient: 'tsc',
  testSignInGrant: 'tsg',
  pullRequestProofRecord: 'proof',
} as const;

export type EntityPrefixValue = (typeof EntityPrefix)[keyof typeof EntityPrefix];

export type EntityRegistryEntry = {
  /** PostgreSQL table name */
  tableName: string;
  /** Column containing the owning org's ID. null for entities that ARE orgs. */
  orgIdColumn: string | null;
  /** Primary key column name (usually 'id') */
  idColumn: string;
  /** Build the frontend URL for this entity. Returns a path string. */
  buildUrl?: (opts: { prefixedId: string; parentPrefixedId?: string }) => string;
  /**
   * Uppercase prefix for this entity's *natural* code, when it has one.
   *
   * A few entities carry a short human-citable code alongside their opaque
   * prefixed id — `CAP-CMM-001` next to `pcap_…`. Both encodings answer "what
   * kind of thing is this?", so both are declared here rather than in a second
   * parallel vocabulary that could drift out of step with this one. Absent for
   * the entities addressed only by prefixed id.
   */
  naturalCodePrefix?: string;
  /** For entities whose URL requires a parent's ID (e.g., test runs need execution ID) */
  parentLookup?: {
    /** Foreign key column on this entity's table pointing to the parent */
    foreignKey: string;
    /** Prefix for encoding the parent entity's ID */
    parentPrefix: EntityPrefixValue;
  };
};

export const ENTITY_REGISTRY: Record<EntityPrefixValue, EntityRegistryEntry> = {
  aeval: { tableName: 'agent_evals', orgIdColumn: 'org_id', idColumn: 'id' },
  aecase: { tableName: 'agent_eval_cases', orgIdColumn: 'org_id', idColumn: 'id' },
  aerun: { tableName: 'agent_eval_runs', orgIdColumn: 'org_id', idColumn: 'id' },
  aeatt: { tableName: 'agent_eval_attempts', orgIdColumn: 'org_id', idColumn: 'id' },
  org: {
    tableName: 'organizations',
    orgIdColumn: null,
    idColumn: 'id',
  },
  wf: {
    tableName: 'workflows',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/flows/${prefixedId}`,
  },
  exec: {
    tableName: 'workflow_test_executions',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/runs/test/${prefixedId}`,
  },
  trun: {
    tableName: 'workflow_test_runs',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ parentPrefixedId }) => `/runs/test/${parentPrefixedId}`,
    parentLookup: {
      foreignKey: 'execution_id',
      parentPrefix: 'exec',
    },
  },
  wrun: {
    tableName: 'workflow_runs',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    // Needs propertyId parent — not yet implemented
  },
  adhoc: {
    tableName: 'adhoc_tests',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/exploratory/${prefixedId}`,
  },
  expt: {
    tableName: 'exploratory_tests',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  ptest: {
    tableName: 'perf_tests',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  prun: {
    tableName: 'perf_runs',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  iss: {
    tableName: 'issues',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/issues?issueId=${prefixedId}`,
  },
  prop: {
    tableName: 'org_properties',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/properties/${prefixedId}`,
  },
  cred: {
    tableName: 'org_property_credentials',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  ctpl: {
    tableName: 'credential_templates',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  su: {
    tableName: 'synthetic_universes',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/synthetic-users/${prefixedId}`,
  },
  sp: {
    tableName: 'synthetic_users',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  sr: {
    tableName: 'synthetic_user_runs',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  uev: {
    tableName: 'user_events',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  ued: {
    tableName: 'user_event_dashboards',
    orgIdColumn: null,
    idColumn: 'id',
  },
  uedc: {
    tableName: 'user_event_dashboard_cards',
    orgIdColumn: null,
    idColumn: 'id',
  },
  trg: {
    tableName: 'triage_runs',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ parentPrefixedId }) =>
      parentPrefixedId ? `/runs/test/${parentPrefixedId}?tab=triage` : '/runs/test',
    parentLookup: {
      foreignKey: 'workflow_test_execution_id',
      parentPrefix: 'exec',
    },
  },
  trgf: {
    tableName: 'triage_findings',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  phn: {
    tableName: 'messaging_phone_numbers',
    orgIdColumn: 'owner_org_id',
    idColumn: 'id',
  },
  sms: {
    tableName: 'text_messages',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  parea: {
    tableName: 'product_areas',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/product-map?area=${prefixedId}`,
    naturalCodePrefix: 'ARE',
  },
  pcap: {
    tableName: 'product_capabilities',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/product-map?capability=${prefixedId}`,
    naturalCodePrefix: 'CAP',
  },
  prule: {
    tableName: 'product_capability_rules',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  // No buildUrl: landing pages are authored from the CLI and served at
  // runcofactor.com/lp/<slug> — there is no in-app detail route to link to.
  //
  // Deliberately NO `lpv` prefix for landing_page_variants. That table has no
  // org_id column, and `orgIdColumn: null` does not mean "child table" here —
  // resolveEntityOrg (apps/api/src/middleware/resolve-entity-org.ts) reads it
  // as "the ID *is* the orgId" and compares the UUID against the caller's org,
  // so a variant routed through the generic middleware would be rejected for
  // the wrong reason. Variants are addressed by (page slug, variant name);
  // add a prefix only together with real parent-resolving lookup support.
  lp: {
    tableName: 'landing_pages',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  prr: {
    tableName: 'pr_reviews',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/pr-reviews/${prefixedId}`,
  },
  dig: {
    tableName: 'org_digests',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/digests/${prefixedId}`,
  },
  excy: {
    tableName: 'exploration_cycles',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/explore/cycles/${prefixedId}`,
  },
  exld: {
    tableName: 'exploration_leads',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  tsp: {
    tableName: 'test_sign_in_providers',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  tsc: {
    tableName: 'test_sign_in_clients',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  tsg: {
    tableName: 'test_sign_in_grants',
    orgIdColumn: 'org_id',
    idColumn: 'id',
  },
  proof: {
    tableName: 'pull_request_proof_records',
    orgIdColumn: 'org_id',
    idColumn: 'id',
    buildUrl: ({ prefixedId }) => `/proofs/${prefixedId}`,
  },
} as const;

// Freeze the registry — sql.raw() safety depends on these being immutable string literals
Object.freeze(ENTITY_REGISTRY);

/** Strict SQL identifier pattern — registry values must match this. */
const SAFE_IDENT = /^[a-z_][a-z0-9_]*$/;

// Validate all registry entries at module load time
for (const [prefix, entry] of Object.entries(ENTITY_REGISTRY)) {
  for (const field of [entry.tableName, entry.orgIdColumn, entry.idColumn].filter(
    Boolean
  ) as string[]) {
    if (!SAFE_IDENT.test(field)) {
      throw new Error(`Invalid identifier in ENTITY_REGISTRY['${prefix}']: "${field}"`);
    }
  }
}

/** All valid prefix strings, derived from the registry. */
export const VALID_PREFIXES = new Set<string>(Object.keys(ENTITY_REGISTRY));

/** Look up a registry entry by prefix string. Returns undefined for unknown prefixes. */
export function getRegistryEntry(prefix: string): EntityRegistryEntry | undefined {
  return ENTITY_REGISTRY[prefix as EntityPrefixValue];
}
